import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import Icons from "../../../assets/Icons";
import CustomTabs from "../../../components/CustomTabs";
import { COPILOT_RECIPIENT } from "../types/CommonTypes";

type TabKey = "users" | "roles" | "departments";

const TAB_I18N: Record<TabKey, string> = {
  users: "Copilot_Recommend_Tab_User",
  roles: "Copilot_Recommend_Tab_Role",
  departments: "Copilot_Recommend_Tab_Department",
};

const RECIPIENT_TABS = (Object.keys(TAB_I18N) as TabKey[]).map((tab) => ({
  id: tab,
  label: TAB_I18N[tab],
}));

const ACCENT = "#5921F2";

interface RecipientPickerProps {
  selectedRecipients: COPILOT_RECIPIENT[];
  recipientOptions?: {
    users?: COPILOT_RECIPIENT[];
    roles?: COPILOT_RECIPIENT[];
    departments?: COPILOT_RECIPIENT[];
  };
  onToggle: (opt: COPILOT_RECIPIENT) => void;
  onRemove: (id: string) => void;
}

export default function RecipientPicker({
  selectedRecipients,
  recipientOptions,
  onToggle,
  onRemove,
}: Readonly<RecipientPickerProps>) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("users");

  const totalCount = selectedRecipients.reduce((sum, r) => sum + r.count, 0);
  const activeOptions: COPILOT_RECIPIENT[] = recipientOptions?.[activeTab] ?? [];
  const isSelected = (id: string) => selectedRecipients.some((r) => r.id === id);

  return (
    <>
      {/* RECIPIENT label */}
      <Typography
        sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "text.primary", mb: 0.75 }}
      >
        {t("Copilot_Recommend_Recipient_Label")}
      </Typography>

      {/* Selected chips + Add button */}
      <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center" mb={0.75}>
        {selectedRecipients.map((r) => (
          <Box
            key={r.id}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              border: "1px solid #D1D5DB",
              borderRadius: "20px",
              px: 1.25,
              py: 0.375,
              bgcolor: "#FFFFFF",
            }}
          >
            <Typography sx={{ fontSize: 13, lineHeight: "20px" }}>
              {r.type === "user" ? r.label : `${r.label} (${r.count})`}
            </Typography>
            <Box
              component="button"
              onClick={() => onRemove(r.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                p: 0,
                color: "#6B7280",
                "&:hover": { color: "#374151" },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </Box>
          </Box>
        ))}
        <Box
          component="button"
          onClick={() => setPickerOpen((prev) => !prev)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            border: "1px dashed #A3B3FF",
            borderRadius: "20px",
            px: 1.5,
            py: 0.625,
            bgcolor: "transparent",
            color: ACCENT,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
            "&:hover": { opacity: 0.85 },
          }}
        >
          {t("Copilot_Recommend_Add_Recipient")}
        </Box>
      </Box>

      {/* Picker dropdown */}
      {pickerOpen && (
        <Box sx={{ border: "1px solid #E5E7EB", borderRadius: "8px", mb: 1, bgcolor: "#FFFFFF" }}>
          <CustomTabs
            tabs={RECIPIENT_TABS}
            activeTab={activeTab}
            onTabChange={(val) => setActiveTab(val as TabKey)}
          />
          <Box sx={{ maxHeight: 220, overflowY: "auto", borderRadius: "0 0 8px 8px" }}>
            {activeOptions.length === 0 ? (
              <Typography sx={{ px: 1.5, py: 1.5, fontSize: 13, color: "text.secondary" }}>
                {t("Copilot_Broadcast_No_Options")}
              </Typography>
            ) : (
              activeOptions.map((opt) => (
                <Box
                  key={opt.id}
                  display="flex"
                  alignItems="center"
                  px={1.5}
                  py={1.25}
                  sx={{ cursor: "pointer", "&:hover": { bgcolor: "#F5F7FF" } }}
                  onClick={() => onToggle(opt)}
                >
                  <Box component="span" sx={{ display: "flex", flexShrink: 0, mr: 1.25 }}>
                    {isSelected(opt.id)
                      ? <Icons.CheckboxFilled width={20} height={20} />
                      : <Icons.CheckboxEmpty width={20} height={20} />}
                  </Box>
                  <Typography sx={{ flex: 1, fontSize: 14, lineHeight: "20px", color: "text.primary" }}>
                    {opt.label}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "text.secondary", flexShrink: 0 }}>
                    {t("Copilot_Recommend_People_Short", { count: opt.count })}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}

      {/* People count */}
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.25 }}>
        {t("Copilot_Recommend_People_Count", { count: totalCount })}
      </Typography>
    </>
  );
}
